/**
 * Mapbox `road` タイルから「避難先境界を横切る歩行可能な道 = 通路の進入点」を抽出する
 * 純ロジック。
 *
 * 避難先が「地区」タイプ (新川ツインビル地区等) だと OSM/Mapbox とも入口 (entrance/gate)
 * データが存在しないことがある (実測 0件)。一方 `road` レイヤーは歩道・小道 (class=path)
 * を含む歩行者ネットワークを持ち、**歩ける線が境界を横切る点 = 実在の通路の入口**として
 * 直接使える (実測: 新川ツインビル地区で path×8 + street×2 の進入点)。
 * 柵・植栽の隙間からの推定 (negative space) はデータ欠如と誤検知が多く、採らない。
 *
 * 取得は services/mapbox-footprint.ts (fetchMapboxBoundaryEntries)、ここは
 * feature 配列 → 進入点の純変換。橋抽出 (bridge-detect.ts) と同じ構成。
 */
import type { LngLat } from '@/types/property';
import type { TileRoadFeature } from './bridge-detect';

/**
 * 進入点として認めない road class。
 * 自動車専用 (motorway/trunk)・鉄道・フェリー・索道・工事中は「歩いて入る通路」でない。
 * (実測: 新川ツインビル地区では major_rail の境界横断が誤検知として現れた)
 */
const UNWALKABLE_ENTRY_CLASS =
  /^(motorway|motorway_link|trunk|trunk_link|major_rail|minor_rail|service_rail|ferry|aerialway|construction)$/;

/** 同一通路の分割 feature (上下線・タイル境界) をまとめる進入点の距離 (m)。 */
const ENTRY_DEDUPE_M = 15;

/**
 * 「境界で行き止まる道」を進入点と認める、道の端点と境界の最大距離 (m)。
 * 街路が地区境界で終わり内部が私道/通路 (未マップ) になっているケースは交差点を
 * 持たないため、交差判定だけでは取りこぼす (実測: 新川ツインビル地区)。
 */
const ENTRY_TOUCH_M = 12;

/**
 * 「街路に面した境界」とみなす、境界サンプル点と歩ける道の最大距離 (m)。
 * 学校など、門のデータ (交差・行き止まり・structure/gate) がタイルに無い施設では、
 * 街路に面した縁の**どこにでも**入口があり得る。並走する街路沿いの境界を等間隔で
 * 候補化し、検出済み入口 (優先) の隙間を埋める (実測: 阪本小学校は明示データ5クラスタ
 * のみだが全周が街路に面している)。
 */
const SAMPLE_ROAD_M = 25;
/** 街路沿いサンプリングの間隔 (m)。周長に応じて [50, 120] にクランプ。 */
const SAMPLE_MIN_INTERVAL_M = 50;
const SAMPLE_MAX_INTERVAL_M = 120;

/** 線分 a-b と c-d の交点 (真に交差する場合のみ)。 */
function segmentIntersection(
  a: [number, number],
  b: [number, number],
  c: [number, number],
  d: [number, number],
): [number, number] | null {
  const r1 = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
  const r2 = (b[0] - a[0]) * (d[1] - a[1]) - (b[1] - a[1]) * (d[0] - a[0]);
  const r3 = (d[0] - c[0]) * (a[1] - c[1]) - (d[1] - c[1]) * (a[0] - c[0]);
  const r4 = (d[0] - c[0]) * (b[1] - c[1]) - (d[1] - c[1]) * (b[0] - c[0]);
  if (r1 * r2 < 0 && r3 * r4 < 0) {
    const t = r3 / (r3 - r4);
    return [a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1])];
  }
  return null;
}

/** 2点間の概算距離 (m)。デデュープ用の局所計算。 */
function approxMeters(a: LngLat, b: LngLat): number {
  const kx = Math.cos(((a.lat + b.lat) / 2) * (Math.PI / 180)) * 111_320;
  return Math.hypot((a.lng - b.lng) * kx, (a.lat - b.lat) * 110_540);
}

/** 点から線分への最近点と距離 (m, メートル空間で射影)。 */
function nearestOnSegmentMeters(
  p: [number, number],
  a: [number, number],
  b: [number, number],
): { point: [number, number]; distM: number } {
  const kx = Math.cos(p[1] * (Math.PI / 180)) * 111_320;
  const ky = 110_540;
  const px = p[0] * kx;
  const py = p[1] * ky;
  const ax = a[0] * kx;
  const ay = a[1] * ky;
  const bx = b[0] * kx;
  const by = b[1] * ky;
  const dx = bx - ax;
  const dy = by - ay;
  const l2 = dx * dx + dy * dy;
  let t = l2 > 0 ? ((px - ax) * dx + (py - ay) * dy) / l2 : 0;
  t = Math.max(0, Math.min(1, t));
  return {
    point: [a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1])],
    distM: Math.hypot(px - (ax + t * dx), py - (ay + t * dy)),
  };
}

/** 点からリング境界への最近点と距離 (m)。 */
function nearestOnRingMeters(
  p: [number, number],
  closed: Array<[number, number]>,
): { point: [number, number]; distM: number } {
  let best: { point: [number, number]; distM: number } = {
    point: closed[0]!,
    distM: Infinity,
  };
  for (let j = 1; j < closed.length; j++) {
    const r = nearestOnSegmentMeters(p, closed[j - 1]!, closed[j]!);
    if (r.distM < best.distM) best = r;
  }
  return best;
}

/** リング周長 (m, 概算)。 */
function ringPerimeterMeters(closed: Array<[number, number]>): number {
  let sum = 0;
  for (let j = 1; j < closed.length; j++) {
    sum += approxMeters(
      { lng: closed[j - 1]![0], lat: closed[j - 1]![1] },
      { lng: closed[j]![0], lat: closed[j]![1] },
    );
  }
  return sum;
}

/** リング境界に沿って ~intervalM 間隔でサンプル点を置く。 */
function samplePointsAlongRing(
  closed: Array<[number, number]>,
  intervalM: number,
): LngLat[] {
  const out: LngLat[] = [];
  let carry = 0;
  for (let j = 1; j < closed.length; j++) {
    const a = closed[j - 1]!;
    const b = closed[j]!;
    const segM = approxMeters(
      { lng: a[0], lat: a[1] },
      { lng: b[0], lat: b[1] },
    );
    if (segM <= 0) continue;
    let d = intervalM - carry;
    while (d <= segM) {
      const t = d / segM;
      out.push({
        lng: a[0] + (b[0] - a[0]) * t,
        lat: a[1] + (b[1] - a[1]) * t,
      });
      d += intervalM;
    }
    carry = (carry + segM) % intervalM;
  }
  return out;
}

/**
 * road feature 群から「リング境界を横切る歩行可能な進入点」を抽出する。
 * 交差 (貫通は両横断点) + 境界で行き止まる道の端点 + 街路沿い境界の等間隔サンプル。
 * 検出済み (交差・行き止まり) を優先し、サンプルは 15m デデュープで隙間だけを埋める。
 */
export function boundaryEntriesFromRoadFeatures(
  features: TileRoadFeature[],
  ring: Array<[number, number]>,
): LngLat[] {
  if (ring.length < 3) return [];
  // 閉じたリング (末尾重複があっても pointInRing/辺走査に影響しない形へ)
  const first = ring[0]!;
  const last = ring[ring.length - 1]!;
  const closed =
    first[0] === last[0] && first[1] === last[1] ? ring : [...ring, first];

  // 歩ける線を先に集める (交差・行き止まり・街路沿いサンプリングで共用)
  const walkLines: Array<Array<[number, number]>> = [];
  for (const f of features) {
    const cls = f.properties?.class ?? '';
    if (UNWALKABLE_ENTRY_CLASS.test(cls)) continue;
    const g = f.geometry;
    if (g.type === 'LineString') {
      walkLines.push(g.coordinates as Array<[number, number]>);
    } else if (g.type === 'MultiLineString') {
      walkLines.push(...(g.coordinates as Array<Array<[number, number]>>));
    }
  }

  const out: LngLat[] = [];
  for (const line of walkLines) {
    for (let i = 1; i < line.length; i++) {
      const a = line[i - 1]!;
      const b = line[i]!;
      // 全セグメント×全辺で交差判定する。「一端が内・他端が外」だけに絞ると、
      // 1セグメントでリングを貫通する道 (両端とも外) の横断点を取りこぼす。
      for (let j = 1; j < closed.length; j++) {
        const x = segmentIntersection(a, b, closed[j - 1]!, closed[j]!);
        if (x) out.push({ lng: x[0], lat: x[1] });
      }
    }
    // 境界で「行き止まる」道 (交差せず端点が境界近傍で終わる) も進入点にする。
    // 対象は線の端点のみ — 境界と並走する貫通道の中間頂点は端点でないため誤検知しない。
    if (line.length >= 2) {
      for (const tip of [line[0]!, line[line.length - 1]!]) {
        const near = nearestOnRingMeters(tip, closed);
        if (near.distM <= ENTRY_TOUCH_M) {
          out.push({ lng: near.point[0], lat: near.point[1] });
        }
      }
    }
  }

  // 街路に面した境界の等間隔サンプル: 門のデータが無い施設 (学校等) では入口が
  // 街路沿いのどこにでもあり得るため、候補として面全体をカバーする。
  const perimeter = ringPerimeterMeters(closed);
  const interval = Math.min(
    SAMPLE_MAX_INTERVAL_M,
    Math.max(SAMPLE_MIN_INTERVAL_M, perimeter / 12),
  );
  for (const sp of samplePointsAlongRing(closed, interval)) {
    let near = Infinity;
    for (const line of walkLines) {
      for (let i = 1; i < line.length; i++) {
        near = Math.min(
          near,
          nearestOnSegmentMeters([sp.lng, sp.lat], line[i - 1]!, line[i]!)
            .distM,
        );
        if (near <= SAMPLE_ROAD_M) break;
      }
      if (near <= SAMPLE_ROAD_M) break;
    }
    if (near <= SAMPLE_ROAD_M) out.push(sp);
  }

  const deduped: LngLat[] = [];
  for (const p of out) {
    if (!deduped.some((d) => approxMeters(d, p) < ENTRY_DEDUPE_M)) {
      deduped.push(p);
    }
  }
  return deduped;
}

/**
 * road feature 群から「点 p に最も近い歩行可能な道路上の点」を返す。
 *
 * 物件側の始点解決に使う: 経路の始点は「目的地方向の縁」でなく「最も近い歩ける道に
 * 面した縁」から出すべき (目的地方向だと、たまたま接する path 等へスナップし、実際の
 * 前面道路と別の側から出発してしまう — 実測: プライムアーバンの南側 path)。
 */
/** 物件の「道路面」とみなす道の最大距離 (m)。 */
const NEARBY_ROAD_MAX_M = 45;
/** 同じ面の並走・分割 feature をまとめる距離 (m)。 */
const NEARBY_ROAD_DEDUPE_M = 25;
/**
 * 返す道路面の最大数。物件の中庭通路など「面でない道」が上位枠を食っても、
 * 四方の街路 (最大4) + α が残るだけの余裕を持たせる (実測: 上限4で西側の
 * 直進道路が枠から溢れ、まっすぐな経路が候補に出なかった)。
 */
const NEARBY_ROAD_MAX = 6;

/**
 * 点 p の近傍にある「歩ける道」を面ごとに列挙する (距離昇順・近接デデュープ)。
 *
 * 物件側の始点候補に使う: 各道路面から実際に経路を引き「経路が最も短くなる面」を
 * 採用し、他の面は経路候補としてユーザーに提示する (どの面が正しい出入り口かは
 * 最終的にユーザーにしか分からない — 避難先の進入点と同じ思想)。
 *
 * ring (物件の輪郭) を渡すと、**輪郭の各頂点からも**最近点を探す。物件座標
 * (中心) だけで探すと、細長い建物で端の面が落ちる — 道 feature ごとに最近点は
 * 1つなので、建物に沿って走る道は中心の真横しか候補にならない
 * (実測: プライムアーバン勝どきは約75mの細長い建物で、北東端の前の道が
 * 候補に出ず、経路が必ず南西側から出ていた)。
 */
export function nearbyRoadPointsFromFeatures(
  features: TileRoadFeature[],
  p: LngLat,
  ring?: Array<[number, number]>,
): LngLat[] {
  const center: [number, number] = [p.lng, p.lat];
  // 中心 + 輪郭の頂点。頂点は密なことがあるので、離れたものだけ使う。
  const targets: Array<[number, number]> = [center];
  for (const v of ring ?? []) {
    if (!targets.some((t) => approxMeters({ lng: t[0], lat: t[1] }, { lng: v[0], lat: v[1] }) < NEARBY_ROAD_DEDUPE_M)) {
      targets.push([v[0], v[1]]);
    }
  }
  const found: Array<{ point: LngLat; distM: number }> = [];
  for (const f of features) {
    const cls = f.properties?.class ?? '';
    if (UNWALKABLE_ENTRY_CLASS.test(cls)) continue;
    const g = f.geometry;
    const lines: Array<Array<[number, number]>> =
      g.type === 'LineString'
        ? [g.coordinates as Array<Array<number>> as Array<[number, number]>]
        : g.type === 'MultiLineString'
          ? (g.coordinates as Array<Array<[number, number]>>)
          : [];
    for (const line of lines) {
      // feature(線) × 探索点ごとに最近点1つ = 1つの「面」候補
      for (const target of targets) {
        let best: { point: [number, number]; distM: number } | null = null;
        for (let i = 1; i < line.length; i++) {
          const r = nearestOnSegmentMeters(target, line[i - 1]!, line[i]!);
          if (!best || r.distM < best.distM) best = r;
        }
        if (best && best.distM <= NEARBY_ROAD_MAX_M) {
          found.push({
            point: { lng: best.point[0], lat: best.point[1] },
            // 並び順は物件座標からの距離で決める (中心に近い面を先に試す)
            distM: approxMeters(p, { lng: best.point[0], lat: best.point[1] }),
          });
        }
      }
    }
  }
  found.sort((a, b) => a.distM - b.distM);
  const out: LngLat[] = [];
  for (const f of found) {
    if (!out.some((d) => approxMeters(d, f.point) < NEARBY_ROAD_DEDUPE_M)) {
      out.push(f.point);
    }
    if (out.length >= NEARBY_ROAD_MAX) break;
  }
  return out;
}

export function nearestRoadPointFromFeatures(
  features: TileRoadFeature[],
  p: LngLat,
): { point: LngLat; distM: number } | null {
  const target: [number, number] = [p.lng, p.lat];
  let best: { point: LngLat; distM: number } | null = null;
  for (const f of features) {
    const cls = f.properties?.class ?? '';
    if (UNWALKABLE_ENTRY_CLASS.test(cls)) continue;
    const g = f.geometry;
    const lines: Array<Array<[number, number]>> =
      g.type === 'LineString'
        ? [g.coordinates as Array<Array<number>> as Array<[number, number]>]
        : g.type === 'MultiLineString'
          ? (g.coordinates as Array<Array<[number, number]>>)
          : [];
    for (const line of lines) {
      for (let i = 1; i < line.length; i++) {
        const r = nearestOnSegmentMeters(target, line[i - 1]!, line[i]!);
        if (!best || r.distM < best.distM) {
          best = { point: { lng: r.point[0], lat: r.point[1] }, distM: r.distM };
        }
      }
    }
  }
  return best;
}
